/**
 * Fase 2B — Lifecycle Review Queue
 *
 * Toont documenten met `lifecycle_review_required = true` — legacy documenten
 * die de startup-migratie NIET zelfstandig kon classificeren omdat er meerdere
 * documenten in dezelfde context bestaan.
 *
 * Belangrijkste use-case: Braima Mack-scenario waar dezelfde klant twee identieke
 * waardefacturen heeft. Deze UI laat de medewerker per document beslissen:
 * Actief · Duplicaat · Geannuleerd · Vervangen.
 *
 * DESIGN PRINCIPES:
 * - GEEN blinde group-actie. Elk document individueel classificeren.
 * - Bij "Actief" kiezen: overige docs in groep BLIJVEN review-required.
 * - Reden verplicht bij duplicaat/geannuleerd/vervangen.
 * - Klantnaam + bedrag + datum + factuurnummer duidelijk zichtbaar voor
 *   veilige besluitvorming.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCcw, AlertTriangle, CheckCircle2, XCircle, Copy,
  History, ChevronRight,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

const DOC_TYPE_LABELS = {
  packing_list: "Paklijst",
  booking_form: "Boekingsformulier",
  value_document: "Waardefactuur",
};

const STATUS_LABELS = {
  actief: "Actief",
  duplicaat: "Duplicaat",
  geannuleerd: "Geannuleerd",
  vervangen: "Vervangen (historisch)",
};

function fmtAmount(amount, currency = "EUR") {
  if (amount == null) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return `${currency} ${n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function LifecycleReviewQueueTab() {
  const { user } = useAuth();
  const canClassify = ["owner", "admin", "manager"].includes(user?.role);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState(null);
  // dialogState: { doc, group, targetStatus }

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/lifecycle/review-queue");
      setGroups(r.data?.groups || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleClassified = () => {
    setDialogState(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="lifecycle-review-queue-tab">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Lifecycle controle nodig
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            Deze documenten zijn tijdens de migratie <strong>niet automatisch</strong> op
            &quot;Actief&quot; gezet omdat er meerdere documenten in dezelfde context bestaan.
            Beoordeel per document: welke is de geldige (Actief), welke is een dubbel
            (Duplicaat), welke moet geannuleerd worden.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="rounded-none"
          data-testid="lifecycle-review-refresh"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <RefreshCcw className="w-4 h-4 mr-1" />
          )}
          Ververs
        </Button>
      </div>

      {loading && groups.length === 0 ? (
        <div className="border border-gray-200 bg-white p-8 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Review-queue laden...
        </div>
      ) : groups.length === 0 ? (
        <div
          className="border border-emerald-200 bg-emerald-50 p-6 text-center"
          data-testid="lifecycle-review-empty"
        >
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <div className="font-chivo font-bold text-[#0A192F]">
            Geen open conflicten
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Alle documenten hebben een duidelijke lifecycle-status.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <ConflictGroupCard
              key={g.context_key}
              group={g}
              canClassify={canClassify}
              onOpenDialog={(doc, targetStatus) =>
                setDialogState({ doc, group: g, targetStatus })
              }
            />
          ))}
        </div>
      )}

      <ResolveDialog
        state={dialogState}
        onClose={() => setDialogState(null)}
        onClassified={handleClassified}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Conflict-groep card
// ────────────────────────────────────────────────────────────

function ConflictGroupCard({ group, canClassify, onOpenDialog }) {
  const docs = group.documents || [];
  const anyDocInGroup = docs[0] || {};

  return (
    <div
      className="border border-amber-300 bg-white"
      data-testid={`lifecycle-conflict-group-${group.context_key}`}
    >
      {/* Header */}
      <div className="bg-amber-50 border-b border-amber-200 p-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-chivo font-bold text-sm text-[#0A192F]">
              {DOC_TYPE_LABELS[group.doc_type] || group.doc_type}
              {" — "}
              {docs.length} conflicterende versies
            </div>
            <div className="text-[11px] text-gray-600 mt-0.5">
              Klant: <strong>{group.customer_name || "—"}</strong>
              {group.source_ref_short ? (
                <>
                  {" · "}Bron: <code className="bg-white px-1 border border-amber-200">
                    {group.source_ref_short}
                  </code>
                </>
              ) : null}
              {group.source_type ? ` · ${group.source_type}` : null}
              {" · "}Reden migratie: <em>{group.review_reason || "—"}</em>
            </div>
          </div>
        </div>
      </div>

      {/* Vergelijkweergave: documents naast elkaar */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-2 text-left">Factuurnummer</th>
              <th className="p-2 text-left">ID</th>
              <th className="p-2 text-left">Aangemaakt</th>
              <th className="p-2 text-right">Bedrag</th>
              <th className="p-2 text-center">Items</th>
              <th className="p-2 text-left">Workflow</th>
              <th className="p-2 text-center">Acties</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((doc) => (
              <tr
                key={doc.id}
                className="border-b border-gray-100 hover:bg-gray-50"
                data-testid={`lifecycle-conflict-row-${doc.id}`}
              >
                <td className="p-2 font-mono text-[11px]">
                  {doc.invoice_number || "—"}
                </td>
                <td className="p-2 font-mono text-[10px] text-gray-500">
                  {doc.id_short}
                </td>
                <td className="p-2 text-[11px]">{fmtDate(doc.created_at)}</td>
                <td className="p-2 text-right font-bold">
                  {fmtAmount(doc.total_amount, doc.currency)}
                </td>
                <td className="p-2 text-center">{doc.item_count || 0}</td>
                <td className="p-2 text-[11px] text-gray-600">
                  {doc.workflow_status || "—"}
                </td>
                <td className="p-2 text-center">
                  {canClassify && (
                    <ActionButtons
                      doc={doc}
                      onSelect={(targetStatus) => onOpenDialog(doc, targetStatus)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-3 bg-blue-50 border-t border-blue-100 text-[11px] text-blue-900">
        💡 Kies per document afzonderlijk. Bij &quot;Actief&quot; blijven de overige
        documenten in deze groep gemarkeerd voor controle — dat is bewuste veiligheid,
        géén bug.
      </div>
    </div>
  );
}

function ActionButtons({ doc, onSelect }) {
  return (
    <div className="flex flex-wrap gap-1 justify-center">
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSelect("actief")}
        className="rounded-none h-7 text-[10px] border-emerald-500 text-emerald-700 hover:bg-emerald-50"
        data-testid={`lifecycle-classify-actief-${doc.id}`}
        title="Markeer als de geldige, actieve versie"
      >
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Actief
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSelect("duplicaat")}
        className="rounded-none h-7 text-[10px] border-amber-500 text-amber-700 hover:bg-amber-50"
        data-testid={`lifecycle-classify-duplicaat-${doc.id}`}
        title="Per ongeluk dubbel aangemaakt"
      >
        <Copy className="w-3 h-3 mr-1" />
        Duplicaat
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSelect("geannuleerd")}
        className="rounded-none h-7 text-[10px] border-rose-500 text-rose-700 hover:bg-rose-50"
        data-testid={`lifecycle-classify-geannuleerd-${doc.id}`}
        title="Ongeldig verklaren"
      >
        <XCircle className="w-3 h-3 mr-1" />
        Annuleer
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSelect("vervangen")}
        className="rounded-none h-7 text-[10px] border-zinc-500 text-zinc-700 hover:bg-zinc-50"
        data-testid={`lifecycle-classify-vervangen-${doc.id}`}
        title="Historische versie (opvolger bestaat elders)"
      >
        <History className="w-3 h-3 mr-1" />
        Vervangen
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Resolve-dialog: bevestig classificatie + reden
// ────────────────────────────────────────────────────────────

function ResolveDialog({ state, onClose, onClassified }) {
  const [reason, setReason] = useState("");
  const [originalId, setOriginalId] = useState("");
  const [replacesId, setReplacesId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason("");
    setOriginalId("");
    setReplacesId("");
  }, [state?.doc?.id, state?.targetStatus]);

  if (!state) return null;
  const { doc, group, targetStatus } = state;
  const target = targetStatus;
  const requiresReason = target !== "actief";

  const submit = async () => {
    if (requiresReason && !reason.trim()) {
      toast.error("Reden is verplicht bij deze classificatie.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        target_status: target,
        reason: reason.trim(),
      };
      if (target === "duplicaat" && originalId.trim()) {
        body.original_document_id = originalId.trim();
      }
      if (target === "vervangen" && replacesId.trim()) {
        body.replaces_document_id = replacesId.trim();
      }
      await adminApi.post(
        `/admin/lifecycle/${group.doc_type}/${doc.id}/resolve-from-review`,
        body,
      );
      toast.success(
        `Document ${doc.invoice_number || doc.id_short} gemarkeerd als ${STATUS_LABELS[target]}.`,
      );
      onClassified();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const otherDocs = (group.documents || []).filter((d) => d.id !== doc.id);

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-lg rounded-none"
        data-testid="lifecycle-resolve-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            Classificeer als {STATUS_LABELS[target] || target}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {DOC_TYPE_LABELS[group.doc_type]}
            {doc.invoice_number ? ` — ${doc.invoice_number}` : ""}
            {" · Klant: "}
            <strong>{doc.customer_name}</strong>
            {" · Bedrag: "}
            <strong>{fmtAmount(doc.total_amount, doc.currency)}</strong>
          </DialogDescription>
        </DialogHeader>

        {target === "actief" && (
          <div className="border-l-4 border-emerald-500 bg-emerald-50 p-2 text-xs text-emerald-900">
            Dit document wordt de geldige, actieve versie in deze context.
            De overige {otherDocs.length} conflicterende document(en) blijven
            gemarkeerd voor controle totdat je ze afzonderlijk classificeert.
          </div>
        )}
        {target === "duplicaat" && (
          <div className="border-l-4 border-amber-500 bg-amber-50 p-2 text-xs text-amber-900">
            Dit document wordt aangemerkt als per ongeluk dubbel aangemaakt.
            Wordt niet gebruikt voor documentenset, ZIP of e-mail; blijft
            zichtbaar in historie.
          </div>
        )}
        {target === "geannuleerd" && (
          <div className="border-l-4 border-rose-500 bg-rose-50 p-2 text-xs text-rose-900">
            Dit document wordt ongeldig verklaard. Wordt niet gebruikt in
            actieve processen; blijft in historie.
          </div>
        )}
        {target === "vervangen" && (
          <div className="border-l-4 border-zinc-500 bg-zinc-50 p-2 text-xs text-zinc-900">
            Dit document wordt gemarkeerd als historische versie. De opvolgende
            (geldige) versie moet elders in de context bestaan.
          </div>
        )}

        {requiresReason && (
          <div>
            <Label className="text-xs">Reden (verplicht)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                target === "duplicaat"
                  ? "Bijv.: Dubbel ingevuld door klant"
                  : target === "geannuleerd"
                    ? "Bijv.: Verkeerde klant, foutieve zending, ..."
                    : "Bijv.: Vervangen door correctie-versie op ..."
              }
              className="rounded-none mt-1"
              data-testid="lifecycle-resolve-reason"
            />
          </div>
        )}

        {target === "duplicaat" && otherDocs.length > 0 && (
          <div>
            <Label className="text-xs">
              Document waarvan dit een duplicaat is (optioneel)
            </Label>
            <Select value={originalId} onValueChange={setOriginalId}>
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="lifecycle-resolve-original"
              >
                <SelectValue placeholder="Kies origineel document..." />
              </SelectTrigger>
              <SelectContent>
                {otherDocs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.invoice_number || d.id_short}
                    {" — "}
                    {fmtAmount(d.total_amount, d.currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {target === "vervangen" && otherDocs.length > 0 && (
          <div>
            <Label className="text-xs">
              Opvolgend document dat deze vervangt (optioneel)
            </Label>
            <Select value={replacesId} onValueChange={setReplacesId}>
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="lifecycle-resolve-replacer"
              >
                <SelectValue placeholder="Kies opvolgende versie..." />
              </SelectTrigger>
              <SelectContent>
                {otherDocs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.invoice_number || d.id_short}
                    {" — "}
                    {fmtAmount(d.total_amount, d.currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none"
            disabled={busy}
            data-testid="lifecycle-resolve-cancel"
          >
            Annuleren
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (requiresReason && !reason.trim())}
            className="rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white"
            data-testid="lifecycle-resolve-confirm"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <ChevronRight className="w-4 h-4 mr-1" />
            )}
            Bevestig classificatie
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
